import type { ComponentProps, ReactNode } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { de } from '@/i18n/de';

/** Centred dialog — the desktop shape of every modal in the app. */
export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

export function DialogOverlay({ className, ...props }: ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      className={cn(
        'fixed inset-0 z-50 bg-black/40 data-[state=open]:animate-in data-[state=closed]:animate-out',
        className,
      )}
      {...props}
    />
  );
}

export interface DialogContentProps extends ComponentProps<typeof DialogPrimitive.Content> {
  /** Renders the small close cross in the top right corner. */
  showClose?: boolean;
  title: string;
  /** Description for assistive technology when no visible hint exists. */
  srDescription?: string;
  children: ReactNode;
}

export function DialogContent({
  className,
  showClose = true,
  title,
  srDescription,
  children,
  ...props
}: DialogContentProps) {
  return (
    <DialogPrimitive.Portal>
      <DialogOverlay />
      <DialogPrimitive.Content
        className={cn(
          // 560px card minus its padding, matching the desktop reference.
          'fixed top-1/2 left-1/2 z-50 w-[min(508px,calc(100vw-32px))] -translate-x-1/2 -translate-y-1/2',
          'max-h-[calc(100dvh-48px)] overflow-y-auto rounded-panel border border-line bg-card px-[22px] py-5 shadow-dialog',
          className,
        )}
        {...props}
      >
        <div className="flex items-start justify-between gap-3 pb-3.5">
          <DialogPrimitive.Title className="text-card font-bold text-fg">{title}</DialogPrimitive.Title>
          {showClose ? (
            <DialogPrimitive.Close
              className="hit-44 -mt-0.5 text-base leading-none text-fg-hint hover:text-fg"
              aria-label={de.a11y.closeDialog}
            >
              <X className="size-4" />
            </DialogPrimitive.Close>
          ) : null}
        </div>
        {srDescription ? (
          <DialogPrimitive.Description className="sr-only">{srDescription}</DialogPrimitive.Description>
        ) : null}
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}
